import React, { useCallback, useId, useMemo, useState } from 'react';
import { Select } from 'ALIASED-antd';
import type { SelectProps as AntdSelectProps } from 'ALIASED-antd';

interface BaseOptionType {
  disabled?: boolean;
  value: string | number;
  label: React.ReactNode;
}

interface DefaultOptionType extends BaseOptionType {
  children?: Omit<DefaultOptionType, 'children'>[];
}

interface WrappedSelectProps<
  ValueType,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
> extends AntdSelectProps<ValueType, OptionType> {
  'data-testid': string;
  ref?: any;
}

export const WrappedAntdSelect = <
  ValueType = string | number,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>(
  props: WrappedSelectProps<ValueType, OptionType>
) => {
  const selectedLabelId = useId();

  const [uncontrolledValue, setUncontrolledValue] = useState(props.defaultValue);
  const currentValue = props.value !== undefined ? props.value : uncontrolledValue;

  const handleChange: AntdSelectProps<ValueType, OptionType>['onChange'] = (value, option) => {
    if (props.value === undefined) setUncontrolledValue(value);
    props.onChange?.(value, option);
  };

  const isSingleMode = !props.mode;
  const isMultipleMode = props.mode === 'multiple' || props.mode === 'tags';
  const { labelRender } = props;

  const buildLabelIdForValue = useCallback(
    (value: string | number) =>
      isSingleMode ? selectedLabelId : `${selectedLabelId}-${encodeURIComponent(String(value))}`,
    [isSingleMode, selectedLabelId]
  );

  const accessibleLabelRender = useMemo<AntdSelectProps<ValueType, OptionType>['labelRender']>(() => {
    if (!isSingleMode && !isMultipleMode) return labelRender;

    return (labelProps) => {
      const label = labelRender ? labelRender(labelProps) : (labelProps.label ?? labelProps.value);
      return (
        <span id={buildLabelIdForValue(labelProps.value)} title={typeof label === 'string' ? label : undefined}>
          {label}
        </span>
      );
    };
  }, [isSingleMode, isMultipleMode, labelRender, buildLabelIdForValue]);

  const selectedValues = (Array.isArray(currentValue) ? currentValue : [currentValue]).map((value) =>
    value && typeof value === 'object' && 'value' in value ? value.value : value
  );

  const getDescribedByLabelIds = () => {
    if (isSingleMode) return [selectedLabelId];
    if (isMultipleMode) {
      return selectedValues.filter((value) => value !== undefined && value !== null).map(buildLabelIdForValue);
    }
    return [];
  };

  const selectedLabelIds = getDescribedByLabelIds();

  const ariaDescribedBy = [props['aria-describedby'], ...selectedLabelIds].filter(Boolean).join(' ') || undefined;

  return (
    <Select
      {...props}
      onChange={handleChange}
      style={{ width: '100%', ...props.style }}
      labelRender={accessibleLabelRender}
      aria-describedby={ariaDescribedBy}
    />
  );
};

WrappedAntdSelect.Option = Select.Option as unknown as React.FC<any>;
WrappedAntdSelect.OptGroup = Select.OptGroup as unknown as React.FC<any>;
